import { useCallback, useMemo, useState } from 'react';
import {
  BookableEntity,
  ScheduleDefinition,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RRule } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { formatDateTime } from '@frontend/schedule-helpers';
import { ModalControlModalProps, useAlert, useForm } from '@frontend/design-system';
import {
  AvailabilityRuleFormConfig,
  AvailabilityMode,
  BookingCadenceOptions,
  PatientBookingOptions,
  AvailabilityRecurrenceTypes,
} from '../../components/AvailabilityRules/AvailabilityRuleForm/types';
import { getAvailabilityRecurrenceTypeFromRRule } from '../../rrule-helper';
import { DATE_DISPLAY_FORMAT, getSameDateWithDifferentTimezone } from '../../utils/date-helpers';

dayjs.extend(utc);

const { DATETIME_FORMATS } = SchedulerV3.Helpers;

type UseAvailabilityRuleFormProps = {
  locationId: string | undefined;
  selectedScheduleDefinition: ScheduleDefinition | undefined | null;
  availabilityMode: AvailabilityMode;
  selectedResourceId: string | undefined;
  modalProps: ModalControlModalProps;
  isEditMode: boolean;
  providerList: { value: string; label: string }[];
  operatoryList: { value: string; label: string }[];
};

const getRRuleObj = (rRuleStr: string | undefined): RRule | undefined => {
  if (rRuleStr) {
    try {
      return RRule.fromString(rRuleStr);
    } catch (e) {
      console.error('Failed to parse RRule:', e);
      return undefined;
    }
  }
  return undefined;
};

export const useAvailabilityRuleForm = ({
  locationId: locationIdProp,
  selectedScheduleDefinition,
  availabilityMode,
  selectedResourceId,
  modalProps,
  isEditMode,
  providerList,
  operatoryList,
}: UseAvailabilityRuleFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const locationId = selectedScheduleDefinition && isEditMode ? selectedScheduleDefinition?.locationId : locationIdProp;
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezone = locationTimezoneMap[locationId ?? '']?.timezone;
  const todayDate = dayjs().format(DATE_DISPLAY_FORMAT);

  const initialRRuleObj = useMemo(
    () => (selectedScheduleDefinition?.rRule ? getRRuleObj(selectedScheduleDefinition?.rRule) : undefined),
    [selectedScheduleDefinition?.rRule]
  );
  const [rRule, setRRule] = useState<RRule | undefined>(initialRRuleObj);

  const firstProvider = (availabilityMode === 'PROVIDER' && providerList[0]?.value) || '';
  const firstOperatory = (availabilityMode === 'OPERATORY' && operatoryList[0]?.value) || '';

  const { serviceableProviderId, operatoryId, billableProviderId } = useMemo(
    () => getBookableEntityInfo(selectedScheduleDefinition?.bookableEntities),
    [selectedScheduleDefinition?.bookableEntities]
  );

  const bookableAppointmentTypeIds = useMemo(
    () => selectedScheduleDefinition?.bookableAppointmentTypes.map((type) => type.id) ?? [],
    [selectedScheduleDefinition?.bookableAppointmentTypes]
  );

  const initialRecurrenceType = useMemo(
    () =>
      (selectedScheduleDefinition?.recurrenceType as AvailabilityRecurrenceTypes) ||
      (initialRRuleObj && getAvailabilityRecurrenceTypeFromRRule(initialRRuleObj)) ||
      'WEEKLY',
    [selectedScheduleDefinition?.recurrenceType, initialRRuleObj]
  );

  const { initialStartDate, initialEndDate, initialStartTime, initialEndTime } = useMemo(
    () => getDateAndTimeForInputFields(selectedScheduleDefinition, timezone),
    [selectedScheduleDefinition, timezone]
  ); // to remove unused variable warning for selectedScheduleDefinition and initialRRuleObj which are used in other useMemos

  const { isComplete, values, getFieldProps, reset, seedValues } = useForm<
    AvailabilityRuleFormConfig,
    keyof AvailabilityRuleFormConfig
  >({
    fields: {
      operatory: {
        type: 'dropdown',
        required: true,
        value:
          (availabilityMode === 'OPERATORY' && selectedResourceId) ||
          (selectedScheduleDefinition && operatoryId) ||
          firstOperatory ||
          '',
      },
      serviceableProvider: {
        type: 'dropdown',
        value:
          (availabilityMode === 'PROVIDER' && selectedResourceId) ||
          (selectedScheduleDefinition && serviceableProviderId) ||
          firstProvider ||
          '',
        required: true,
      },
      billableProvider: {
        type: 'dropdown',
        value: selectedScheduleDefinition ? billableProviderId : firstProvider,
        required: true,
      },
      displayName: {
        type: 'text',
        value: selectedScheduleDefinition?.displayName || '',
        required: true,
      },
      recurrenceType: {
        type: 'dropdown',
        value: initialRecurrenceType,
        required: true,
      },
      services: {
        type: 'checklist',
        value: selectedScheduleDefinition ? bookableAppointmentTypeIds : [],
        required: true,
      },
      bookingCadence: {
        type: 'dropdown',
        value: selectedScheduleDefinition ? (selectedScheduleDefinition?.cadence as BookingCadenceOptions) : 'NONE',
        required: true,
      },
      patientBooking: {
        type: 'radio',
        value: selectedScheduleDefinition
          ? (selectedScheduleDefinition?.bookingStrategy as PatientBookingOptions)
          : 'DIRECT',
        required: true,
      },
      startDate: { type: 'date', value: (selectedScheduleDefinition && initialStartDate) || todayDate, required: true },
      endDate: { type: 'date', value: (selectedScheduleDefinition && initialEndDate) || '' },
      startTime: { type: 'time', value: (selectedScheduleDefinition && initialStartTime) || '', required: true },
      endTime: { type: 'time', value: (selectedScheduleDefinition && initialEndTime) || '', required: true },
    },
  });

  const createAvailabilityRuleMutation = SchedulerV3.Mutations.useCreateScheduleDefinitionMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Service type created successfully'));
        modalProps.onClose();
      },
      onError: () => {
        alert.error(t('Failed to create service type'));
      },
    },
  });

  const updateAvailabilityRuleMutation = SchedulerV3.Mutations.useUpdateScheduleDefinitionMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Service type updated successfully'));
        modalProps.onClose();
      },
      onError: () => {
        alert.error(t('Failed to update service type'));
      },
    },
  });

  const handleSubmit = async () => {
    if (
      !values.recurrenceType ||
      !values.services ||
      values.services.length === 0 ||
      !values.startDate ||
      !values.startTime ||
      !values.endTime ||
      !values.displayName
    ) {
      alert.error(t('Please fill in all required fields'));
      return;
    }
    const bookableEntities = formBookableEntityInfo({
      availabilityMode,
      serviceableProviderId: values.serviceableProvider,
      operatoryId: values.operatory,
      billableProviderId: values.billableProvider,
    });

    const startDate = requestDateFormatter(values.startDate, timezone);
    const endDateString = values.endDate ? requestDateFormatter(values.endDate, timezone) : undefined;
    const endDate = values.recurrenceType === 'SPECIFIC_DATE' ? startDate : endDateString ?? '';
    const startTime = requestTimeFormatter(values.startTime, timezone);
    const endTime = requestTimeFormatter(values.endTime, timezone);

    const commonPayload = {
      displayName: values.displayName,
      locationId: locationId ?? '',
      cadence: values.bookingCadence,
      bookingStrategy: values.patientBooking || 'DIRECT',
      recurrenceType: values.recurrenceType,
      bookableAppointmentTypeIds: values.services ?? [],
      bookableEntities,
      rRule: rRule ? rRule.toString().replace(/^RRULE:/, '') : '',
      startDate,
      endDate,
      startTime,
      endTime,
    };

    if (isEditMode) {
      updateAvailabilityRuleMutation.mutateAsync({
        ...commonPayload,
        id: selectedScheduleDefinition?.id ?? '',
      });
    } else {
      createAvailabilityRuleMutation.mutateAsync({
        ...commonPayload,
      });
    }
  };

  const handleRRuleChange = useCallback((rrule: RRule | undefined) => {
    setRRule(rrule);
  }, []);

  return {
    getFieldProps,
    values,
    isComplete,
    reset,
    handleSubmit,
    seedValues,
    handleRRuleChange,
    rRule,
  };
};

export const getBookableEntityInfo = (bookableEntities: BookableEntity[] | undefined) => {
  const mainEntityInfo = bookableEntities?.find((entity) => entity.bucketType === 'BOOKING');
  const bookableEntityType = mainEntityInfo?.resourceType;
  const writeBackEntityInfoList =
    bookableEntities?.filter(
      (entity) => entity.bucketType === 'WRITEBACK' && entity.resourceId !== mainEntityInfo?.resourceId
    ) ?? [];
  const billableProviderInfo = writeBackEntityInfoList.find((entity) => entity.resourceType === 'PROVIDER');
  const billableProviderId = billableProviderInfo?.resourceId ?? '';
  const remainingWritebackEntity = writeBackEntityInfoList.find((entity) => entity.resourceId !== billableProviderId);
  const displayEntityInfo = bookableEntities?.find(
    (entity) => entity.bucketType === 'DISPLAY' && entity.resourceId !== billableProviderId
  );

  const serviceableProviderId =
    (bookableEntityType === 'PROVIDER' ? mainEntityInfo?.resourceId : displayEntityInfo?.resourceId) ?? '';
  const operatoryId =
    (bookableEntityType === 'OPERATORY' ? mainEntityInfo?.resourceId : remainingWritebackEntity?.resourceId) ?? '';

  return { serviceableProviderId, operatoryId, billableProviderId };
};

const formBookableEntityInfo = ({
  availabilityMode,
  serviceableProviderId,
  operatoryId,
  billableProviderId,
}: {
  availabilityMode: AvailabilityMode;
  serviceableProviderId: string | undefined;
  operatoryId: string | undefined;
  billableProviderId: string | undefined;
}) => {
  const bookableEntities: BookableEntity[] = [];
  if (serviceableProviderId) {
    if (availabilityMode === 'PROVIDER') {
      bookableEntities.push({ bucketType: 'BOOKING', resourceId: serviceableProviderId, resourceType: 'PROVIDER' });
    }
    bookableEntities.push({ bucketType: 'DISPLAY', resourceId: serviceableProviderId, resourceType: 'PROVIDER' });
  }
  if (operatoryId) {
    if (availabilityMode === 'OPERATORY') {
      bookableEntities.push({ bucketType: 'BOOKING', resourceId: operatoryId, resourceType: 'OPERATORY' });
    }
    bookableEntities.push({ bucketType: 'WRITEBACK', resourceId: operatoryId, resourceType: 'OPERATORY' });
  }
  if (billableProviderId) {
    bookableEntities.push(
      { bucketType: 'WRITEBACK', resourceId: billableProviderId, resourceType: 'PROVIDER' },
      { bucketType: 'DISPLAY', resourceId: billableProviderId, resourceType: 'PROVIDER' }
    );
  }
  return bookableEntities;
};

const getDateAndTimeForInputFields = (
  selectedScheduleDefinition: ScheduleDefinition | null | undefined,
  timezone: string
) => {
  const initialStartDate = selectedScheduleDefinition?.startDate
    ? initialDateFormatter(selectedScheduleDefinition.startDate, timezone)
    : '';
  const initialEndDate = selectedScheduleDefinition?.endDate
    ? initialDateFormatter(selectedScheduleDefinition.endDate, timezone)
    : '';
  const initialStartTime = selectedScheduleDefinition?.startTime
    ? initialTimeFormatter(selectedScheduleDefinition.startTime, timezone)
    : '';
  const initialEndTime = selectedScheduleDefinition?.endTime
    ? initialTimeFormatter(selectedScheduleDefinition.endTime, timezone)
    : '';

  return { initialStartDate, initialEndDate, initialStartTime, initialEndTime };
};

/**
 * The API expects date and time in ISO format for the timezone of the location.
 * However, the form works with local date and time in MM/DD/YYYY and HH:mm format respectively.
 * These formatter functions convert between these formats for the request payload and for setting initial form values.
 */
const requestDateFormatter = (dateString: string, timezone: string): string => {
  return getSameDateWithDifferentTimezone(dateString, DATE_DISPLAY_FORMAT, timezone).format(DATETIME_FORMATS.ISO_DATE);
};
const requestTimeFormatter = (timeString: string, timezone: string): string => {
  return getSameDateWithDifferentTimezone(timeString, DATETIME_FORMATS.ISO_TIME, timezone).format(
    DATETIME_FORMATS.HOURS_MINUTES
  );
};

/**
 * The API returns date and time in ISO format. To display the existing values in the form for editing,
 * we need to convert them to local date and time in MM/DD/YYYY and HH:mm format respectively based on the timezone of the location.
 */
const initialDateFormatter = (dateString: string, timezone: string): string => {
  return formatDateTime(dateString, {
    inputFormat: DATETIME_FORMATS.ISO_DATE,
    outputFormat: DATE_DISPLAY_FORMAT,
    toTimezone: timezone,
  });
};
const initialTimeFormatter = (timeString: string, timezone: string): string => {
  return formatDateTime(timeString, {
    inputFormat: DATETIME_FORMATS.HOURS_MINUTES,
    outputFormat: DATETIME_FORMATS.ISO_TIME,
    toTimezone: timezone,
  });
};
